/**
 * Redemptions waiting to move money.
 *
 * A client's way out of a fund is the withdrawal screen; an approved withdrawal
 * of a fund product enters the redemption machinery on its own. What this screen
 * shows is the window in between: a redemption a client asked for directly sits
 * in `requested` until a manager approves it and the drain sees `eligible_at`
 * pass; then, in one transaction, the drain posts the ledger debit and the
 * outbox event.
 *
 * In that window a manager approves it or anyone on staff stops it — and it is
 * the only moment where stopping costs nothing. Afterwards, undoing means the
 * reversal path.
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import redemptionsService, { type RedemptionRequest } from '../../services/redemptions.service';
import authService from '../../services/auth.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { approveMfaErrorMessage } from '../../lib/mfaApproval';
import { formatDate, formatDateTime, formatMoney } from '../../lib/datetime';
import { calendarDate } from '../../components/client/enteringFund.helpers';
import { colors } from '../../styles/theme';
import {
  awaitsApproval,
  canApprove,
  noticeLine,
  canStop,
  ESTATE_STOP_REASON_MIN,
  clientLabel,
  describeEligibility,
  sortByUrgency,
  whyCannotCancel,
} from './redemptions.helpers';

const ELIGIBILITY_TONE: Record<'due' | 'soon' | 'waiting', BadgeTone> = {
  due: 'danger',
  soon: 'warning',
  waiting: 'neutral',
};

export default function Redemptions(): JSX.Element {
  const [rows, setRows] = useState<RedemptionRequest[]>([]);
  // Requests an exit window of a series priced at NAV paid only in part: the rest waits for the next window.
  const [deferred, setDeferred] = useState<RedemptionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  // Captured WITH the rows, so every row on screen is measured against the same
  // instant. Reading the clock per row would let two rows be compared against
  // two different "nows", and the ordering is the whole point of the screen.
  const [now, setNow] = useState(() => new Date());

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [page, partial] = await Promise.all([
        redemptionsService.list({ status: ['requested'], size: 100 }),
        redemptionsService.list({ status: ['partially_filled'], size: 100 }),
      ]);
      setRows(page.items);
      setDeferred(partial.items);
      setNow(new Date());
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load redemptions.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const ordered = sortByUrgency(rows, now);

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Redemptions waiting"
        subtitle="Money a client asked to take out of a fund, not yet taken from their balance. It leaves only after a manager approves it and its date comes. Stopping one is free until then."
      />

      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {error}
        </div>
      )}
      {flash && (
        <div className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.success }}>
          {flash}
        </div>
      )}

      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}

      {!loading && ordered.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>
          Nothing is waiting to leave a fund right now.
        </p>
      )}

      <DeferredByWindow rows={deferred} />

      <ul className="space-y-3">
        {ordered.map((r) => (
          <RedemptionCard
            key={r.id}
            redemption={r}
            now={now}
            onDone={(msg) => { setFlash(msg); void reload(); }}
            onError={setError}
          />
        ))}
      </ul>
    </div>
  );
}

function RedemptionCard({ redemption, now, onDone, onError }: Readonly<{
  redemption: RedemptionRequest;
  now: Date;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}>): JSX.Element {
  const [panel, setPanel] = useState<'approve' | 'stop' | null>(null);
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const waiting = awaitsApproval(redemption);
  const eligibility = describeEligibility(redemption.eligible_at, now, !waiting);
  const estate = redemption.given_for_estate === true;
  const mayCancel = canStop(redemption, authService.getRole());
  const reasonShort = estate && reason.trim().length < ESTATE_STOP_REASON_MIN;
  const mayApprove = canApprove(redemption, authService.getRole());
  const blockedWhy = whyCannotCancel(redemption);
  const notice = noticeLine(redemption, calendarDate);
  const open = panel === 'stop';

  const approve = async (): Promise<void> => {
    setBusy(true);
    try {
      await redemptionsService.approve(redemption.id, redemption.version, code.trim());
      onDone(`${redemption.reference_number} approved. It leaves the client’s balance on its date.`);
    } catch (e: unknown) {
      onError(approveMfaErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (): Promise<void> => {
    setBusy(true);
    try {
      await redemptionsService.cancel(redemption.id, redemption.version, reason.trim());
      onDone(`${redemption.reference_number} cancelled. Nothing left the client’s balance.`);
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not cancel this redemption.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-2xl p-4"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-semibold" style={{ color: colors.ink }}>
            {formatMoney(redemption.amount, redemption.currency_code)} · {redemption.product_name}
          </div>
          <div className="text-xs mt-0.5" style={{ color: colors.muted }}>
            {clientLabel(redemption)} · {redemption.reference_number} · requested{' '}
            {formatDateTime(redemption.created_at)}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {redemption.status === 'requested' && (
            <StatusBadge tone={waiting ? 'warning' : 'success'}>
              {waiting ? 'Awaiting approval' : 'Approved'}
            </StatusBadge>
          )}
          <StatusBadge tone={ELIGIBILITY_TONE[eligibility.state]}>{eligibility.text}</StatusBadge>
          {mayApprove && (
            <button type="button" onClick={() => setPanel((v) => (v === 'approve' ? null : 'approve'))}
                    className="text-xs px-3 py-1.5 rounded-md font-semibold"
                    style={{ background: colors.navy, color: '#FFFFFF' }}>
              {panel === 'approve' ? 'Not now' : 'Approve'}
            </button>
          )}
          {mayCancel && (
            <button type="button" onClick={() => setPanel((v) => (v === 'stop' ? null : 'stop'))}
                    className="text-xs px-3 py-1.5 rounded-md font-semibold"
                    style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
              {open ? 'Keep it' : 'Stop it'}
            </button>
          )}
        </div>
      </div>

      {notice && (
        <p className="text-xs mt-2 font-semibold" style={{ color: colors.inkSoft }}>{notice}</p>
      )}
      {redemption.given_for_estate && (
        <p className="text-xs mt-1" style={{ color: colors.inkSoft }}>
          Given for the estate of a deceased holder. A second person approves it, and its payment is
          held until the estate can be settled.
        </p>
      )}

      {waiting && !mayApprove && (
        <p className="text-xs mt-2" style={{ color: colors.muted }}>
          A manager has to approve this before it can leave the client’s balance.
        </p>
      )}

      {panel === 'approve' && mayApprove && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
          <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>
            Approving lets this amount leave the client’s balance on its date and go to the fund.
            To decline it, stop it and say why.
          </p>
          <label htmlFor={`approve-${redemption.id}`} className="block text-xs font-semibold mb-1"
                 style={{ color: colors.inkSoft }}>
            Your authentication code
          </label>
          <input
            id={`approve-${redemption.id}`}
            type="text" inputMode="numeric" autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="6-digit code"
            disabled={busy}
            className="px-3 py-2 rounded-lg text-sm font-mono tracking-widest mb-3 block"
            style={{ border: `1px solid ${colors.border}` }}
          />
          <button type="button" onClick={() => { void approve(); }} disabled={busy || code.length < 6}
                  className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                  style={{ background: colors.navy, color: '#FFFFFF' }}>
            {busy ? 'Approving…' : 'Approve this redemption'}
          </button>
          {code.length < 6 && (
            <p className="text-xs mt-2" style={{ color: colors.muted }}>
              Enter the current code from your authenticator to approve.
            </p>
          )}
        </div>
      )}

      {blockedWhy && (
        <p className="text-xs mt-2" style={{ color: colors.muted }}>{blockedWhy}</p>
      )}

      {open && mayCancel && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
          <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>
            {estate
              ? 'This exit was given for the estate of a deceased holder. Stopping it keeps the holding renewing, so say why in at least ten characters: it is kept with the request.'
              : 'The client agreed to this exit. Cancelling it is a decision someone will ask about, so say why — it is kept with the request.'}
          </p>
          <label htmlFor={`cancel-${redemption.id}`} className="block text-xs font-semibold mb-1"
                 style={{ color: colors.inkSoft }}>
            Why are you stopping it?
          </label>
          <textarea
            id={`cancel-${redemption.id}`}
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 2000))}
            placeholder="The client called to withdraw the request before it settles."
            disabled={busy}
            className="w-full px-3 py-2 rounded-lg text-sm mb-3"
            style={{ border: `1px solid ${colors.border}` }}
          />
          <button type="button" onClick={() => { void cancel(); }} disabled={busy || reasonShort}
                  className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                  style={{ background: colors.navy, color: '#FFFFFF' }}>
            {busy ? 'Stopping…' : 'Stop this redemption'}
          </button>
        </div>
      )}
    </li>
  );
}

/** The part an exit window's gate deferred (S-47): what was paid, what waits and the window it waits for. */
function DeferredByWindow({ rows: all }: Readonly<{ rows: RedemptionRequest[] }>): JSX.Element | null {
  const rows = all.filter((r) => r.status === 'partially_filled' && r.amount_remaining != null);
  if (rows.length === 0) return null;
  return (
    <section className="mb-6">
      <h2 className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: colors.inkSoft }}>
        Deferred by an exit window
      </h2>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.id} className="rounded-xl p-3 text-sm"
              style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.ink }}>
            <div className="font-medium">{`${r.user_first_name} ${r.user_last_name} · ${r.product_name} · ${r.reference_number}`}</div>
            <div style={{ color: colors.inkSoft }}>
              {`Waiting: ${formatMoney(r.amount_remaining ?? '0', r.currency_code)} of ${formatMoney(r.amount, r.currency_code)}, at the window of ${formatDate(r.eligible_at)}`}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
